import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, MessageCircle, FileText, FileDown, AlertCircle, Clock, Loader2, Users, Inbox, X, Filter, Sparkles, Calendar, AlertTriangle, Timer, AlertOctagon, PackageX, CheckCircle2, XCircle, ChevronDown } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import { useOutstanding, useOutstandingBillDetails } from "@/lib/api/hooks/useOutstanding";
import { authService } from "@/lib/api/services/auth.service";
import { outstandingService } from "@/lib/api/services/outstanding.service";
import { formatINR, cn } from "@/lib/utils";

// Modern premium stat card component
interface StatCardProps {
  title: string;
  value: React.ReactNode;
  icon: React.ElementType;
  variant?: "blue" | "green" | "cyan" | "amber" | "orange" | "red";
  className?: string;
}

const StatCard = ({ title, value, icon: Icon, variant = "blue", className }: StatCardProps) => {
  const variants = {
    blue: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(0,114,181,0.4)]",
      iconBg: "from-[#0072B5] via-[#0090B5] to-[#00B5B0]",
      accentLine: "from-[#0072B5] via-[#0090B5] to-[#00B5B0]",
      textGradient: "from-[#0072B5] to-[#00B5B0]",
      glowColor: "rgba(0,114,181,0.15)",
    },
    green: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(34,197,94,0.4)]",
      iconBg: "from-green-500 via-green-600 to-emerald-600",
      accentLine: "from-green-500 via-emerald-500 to-teal-500",
      textGradient: "from-green-500 to-emerald-600",
      glowColor: "rgba(34,197,94,0.15)",
    },
    cyan: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(0,181,176,0.4)]",
      iconBg: "from-[#00B5B0] via-[#0090B5] to-[#0072B5]",
      accentLine: "from-[#00B5B0] via-[#0090B5] to-[#0072B5]",
      textGradient: "from-[#00B5B0] to-[#0072B5]",
      glowColor: "rgba(0,181,176,0.15)",
    },
    amber: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(245,158,11,0.4)]",
      iconBg: "from-amber-500 via-orange-500 to-orange-600",
      accentLine: "from-amber-500 via-orange-500 to-red-500",
      textGradient: "from-amber-500 to-orange-600",
      glowColor: "rgba(245,158,11,0.15)",
    },
    orange: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(249,115,22,0.4)]",
      iconBg: "from-orange-500 via-orange-600 to-red-600",
      accentLine: "from-orange-500 via-red-500 to-rose-500",
      textGradient: "from-orange-500 to-red-600",
      glowColor: "rgba(249,115,22,0.15)",
    },
    red: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(239,68,68,0.4)]",
      iconBg: "from-red-500 via-rose-500 to-pink-600",
      accentLine: "from-red-500 via-rose-500 to-pink-500",
      textGradient: "from-red-500 to-pink-600",
      glowColor: "rgba(239,68,68,0.15)",
    },
  };

  const config = variants[variant];

  return (
    <Card className={cn(
      "group relative overflow-hidden rounded-2xl",
      "bg-gradient-to-br from-background/95 via-background/90 to-muted/50",
      "backdrop-blur-xl",
      "border border-white/10 dark:border-white/5",
      "hover:border-white/20 dark:hover:border-white/10",
      "transition-all duration-500 ease-out",
      "hover:-translate-y-1",
      config.glow,
      className
    )}>
      {/* Animated gradient border */}
      <div className={cn(
        "absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500",
        "bg-gradient-to-r p-[1px]",
        config.accentLine
      )}>
        <div className="absolute inset-[1px] rounded-2xl bg-background/95" />
      </div>
      
      {/* Background glow orbs */}
      <div 
        className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-60 transition-all duration-700"
        style={{ background: `radial-gradient(circle, ${config.glowColor}, transparent 70%)` }}
      />
      <div 
        className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-2xl opacity-0 group-hover:opacity-40 transition-all duration-700 delay-100"
        style={{ background: `radial-gradient(circle, ${config.glowColor}, transparent 70%)` }}
      />
      
      {/* Mesh pattern overlay */}
      <div className="absolute inset-0 opacity-[0.02] group-hover:opacity-[0.04] transition-opacity duration-500" 
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
          backgroundSize: '24px 24px'
        }}
      />
      
      <div className="relative p-3 sm:p-5">
        <div className="flex items-start justify-between gap-2 sm:gap-4">
          {/* Icon with enhanced styling */}
          <div className="relative">
            {/* Icon glow */}
            <div 
              className={cn(
                "absolute inset-0 rounded-2xl blur-xl opacity-40 group-hover:opacity-70 transition-opacity duration-500",
                `bg-gradient-to-br ${config.iconBg}`
              )}
            />
            <div className={cn(
              "relative p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl",
              `bg-gradient-to-br ${config.iconBg}`,
              "shadow-lg",
              "ring-1 ring-white/25",
              "group-hover:scale-110 group-hover:rotate-[-6deg] transition-all duration-500 ease-out"
            )}>
              <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-white drop-shadow-md" strokeWidth={2.5} />
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 text-right min-w-0">
            <p className="text-[10px] sm:text-xs font-medium text-muted-foreground/70 uppercase tracking-wider mb-0.5 sm:mb-1 whitespace-nowrap">
              {title}
            </p>
            <div className={cn(
              "text-lg sm:text-xl md:text-2xl font-bold tracking-tight",
              "bg-gradient-to-r bg-clip-text text-transparent",
              config.textGradient
            )}>
              {value}
            </div>
          </div>
        </div>
        
        {/* Bottom decorative element */}
        <div className="mt-4 flex items-center gap-2">
          <div className={cn(
            "flex-1 h-1 rounded-full bg-gradient-to-r opacity-20 group-hover:opacity-40 transition-opacity duration-500",
            config.accentLine
          )} />
          <div className={cn(
            "w-1.5 h-1.5 rounded-full bg-gradient-to-r",
            config.accentLine,
            "animate-pulse"
          )} />
        </div>
      </div>
    </Card>
  );
};

// Skeleton loader for stat cards
const StatCardSkeleton = () => (
  <Card className="relative overflow-hidden rounded-xl glass-card border-white/10 dark:border-white/5">
    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-muted via-muted-foreground/20 to-muted animate-pulse" />
    <CardHeader className="pb-2 pt-4">
      <div className="flex items-start justify-between gap-3">
        <div className="h-4 w-20 bg-muted rounded animate-pulse" />
        <div className="p-2.5 rounded-xl bg-muted animate-pulse">
          <div className="h-4 w-4" />
        </div>
      </div>
    </CardHeader>
    <CardContent className="pt-1 pb-4">
      <div className="h-8 w-28 bg-muted rounded animate-pulse" />
    </CardContent>
  </Card>
);

// Table row skeleton
const TableRowSkeleton = () => (
  <tr className="border-b h-9">
    <td className="px-4 py-1.5"><div className="h-4 w-6 bg-muted rounded animate-pulse" /></td>
   {/* <td className="px-4 py-1.5"><div className="h-4 w-40 bg-muted rounded animate-pulse" /></td>*/}
    <td className="px-4 py-1.5"><div className="h-4 w-24 bg-muted rounded animate-pulse" /></td>
    <td className="px-2 py-1.5"><div className="h-4 w-24 bg-muted rounded animate-pulse" /></td>
    <td className="px-4 py-1.5"><div className="h-4 w-20 bg-muted rounded animate-pulse ml-auto" /></td>
    <td className="px-2 py-1.5"><div className="h-4 w-14 bg-muted rounded animate-pulse ml-auto" /></td>
    <td className="px-2 py-1.5"><div className="h-4 w-14 bg-muted rounded animate-pulse ml-auto" /></td>
    <td className="px-2 py-1.5"><div className="h-4 w-14 bg-muted rounded animate-pulse ml-auto" /></td>
    <td className="px-2 py-1.5"><div className="h-4 w-14 bg-muted rounded animate-pulse ml-auto" /></td>
    <td className="px-2 py-1.5"><div className="h-4 w-14 bg-muted rounded animate-pulse ml-auto" /></td>
    <td className="px-2 py-1.5"><div className="h-6 w-14 bg-muted rounded animate-pulse mx-auto" /></td>
  </tr>
);

// Aging value display with color coding
interface AgingValueProps {
  value: number;
  variant: "green" | "cyan" | "amber" | "orange" | "red";
}

const AgingValue = ({ value, variant }: AgingValueProps) => {
  if (value === 0) return <span className="text-xs sm:text-sm text-muted-foreground/40">-</span>;
  
  const styles = {
    green: "text-green-600 dark:text-green-400",
    cyan: "text-cyan-600 dark:text-cyan-400", 
    amber: "text-amber-600 dark:text-amber-400",
    orange: "text-orange-600 dark:text-orange-400",
    red: "text-red-600 dark:text-red-400 font-bold",
  };

  return (
    <span className={cn("text-xs sm:text-sm font-medium tabular-nums", styles[variant])}>
      {formatINR(value)}
    </span>
  );
};

// Lakhs-only compact formatter for summary stat cards
const formatLakhsCompact = (amount: number): string => {
  const value = Number.isFinite(amount) ? amount : 0;
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const lakhs = absValue / 100000;
  return `${sign}₹${lakhs.toFixed(2)} L`;
};

// Transformed Outstanding type for component
interface TransformedOutstanding {
  customer: string;
  partyCode: string;
  city: string;
  mobile: string;
  total: number;
  aging: { lt15: number; d15_30: number; d30_60: number; d60_90: number; gt90: number };
  bills: Array<{
    billNo: string;
    date: string;
    amount: number;
    days: number;
  }>;
}

// Helper function to parse date in format "15/Aug/2025" and calculate days
const calculateDays = (dateStr: string): number => {
  try {
    const [day, month, year] = dateStr.split("/");
    const monthMap: Record<string, number> = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11
    };
    const invoiceDate = new Date(parseInt(year), monthMap[month], parseInt(day));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    invoiceDate.setHours(0, 0, 0, 0);
    const diffTime = today.getTime() - invoiceDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 ? diffDays : 0;
  } catch {
    return 0;
  }
};

// Get days badge variant based on age
const getDaysBadgeStyle = (days: number) => {
  if (days > 90) return "bg-gradient-to-r from-red-500 to-rose-600 text-white border-0 shadow-md shadow-red-500/20";
  if (days > 60) return "bg-gradient-to-r from-orange-500 to-red-500 text-white border-0 shadow-md shadow-orange-500/20";
  if (days > 30) return "bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-md shadow-amber-500/20";
  if (days > 15) return "bg-gradient-to-r from-cyan-500 to-teal-500 text-white border-0 shadow-md shadow-cyan-500/20";
  return "bg-gradient-to-r from-green-500 to-emerald-500 text-white border-0 shadow-md shadow-green-500/20";
};

// Bill Details Dialog Component
interface BillDetailsDialogProps {
  customer: string;
  partyCode: string;
  firmId: number;
  total: number;
}

const BillDetailsDialog = ({ customer, partyCode, firmId }: BillDetailsDialogProps) => {
  const [open, setOpen] = useState(false);
  
  const { data: billDetails, isLoading, error } = useOutstandingBillDetails({
    firmId,
    partyCode,
    mode: 0,
    enabled: open,
  });

  const bills = useMemo(() => {
    if (!billDetails) return [];
    return billDetails.map((bill) => ({
      billNo: bill.invoiceNo,
      date: bill.invoiceDate,
      amount: bill.totalBalance,
      days: bill.dueDays || 0,
    }));
  }, [billDetails]);

  const totalAmount = useMemo(() => {
    return bills.reduce((sum, bill) => sum + bill.amount, 0);
  }, [bills]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          size="sm" 
          variant="outline" 
          className="h-7 w-7 p-0 rounded-lg border-border/50 hover:border-primary/50 hover:bg-primary/5 transition-all duration-200 group"
        >
          <FileText className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-4xl w-[95vw] sm:w-full overflow-hidden p-0 gap-0 glass-card border-white/20">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#0072B5] via-[#0090B5] to-[#00B5B0]" />
        <DialogHeader className="p-4 sm:p-5 pb-3 sm:pb-4 border-b border-border/50">
          <DialogTitle className="flex items-center gap-2 sm:gap-3 text-lg sm:text-xl">
            <div className="p-1.5 sm:p-2 rounded-lg bg-gradient-to-br from-[#0072B5] to-[#00B5B0] shadow-lg">
              <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
            </div>
            <div className="min-w-0">
              <span className="text-foreground block truncate">Bill-wise Details</span>
              <p className="text-xs sm:text-sm font-normal text-muted-foreground mt-0.5 truncate">{customer}</p>
            </div>
          </DialogTitle>
          <DialogDescription className="sr-only">
            View detailed bill information for {customer}
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto max-h-[calc(90vh-100px)] p-3 sm:p-5">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted/50 mb-4">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
              <p className="text-sm text-muted-foreground">Loading bill details...</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-4">
                <AlertCircle className="h-8 w-8 text-destructive" />
              </div>
              <p className="text-sm text-destructive">Error loading bill details. Please try again.</p>
            </div>
          ) : bills.length > 0 ? (
            <>
              {/* Mobile Card View */}
              <div className="block md:hidden space-y-3">
                {bills.map((bill, billIdx) => (
                  <Card key={billIdx} className="p-3 border-border/50">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-muted text-xs font-medium">
                          {billIdx + 1}
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-foreground">{bill.billNo}</p>
                          <p className="text-xs text-muted-foreground">{bill.date}</p>
                        </div>
                      </div>
                      <Badge className={cn("text-xs px-2 py-0 shrink-0", getDaysBadgeStyle(bill.days))}>
                        {bill.days} days
                      </Badge>
                    </div>
                    <div className="pt-2 border-t border-border/30">
                      <p className="text-sm font-bold text-primary text-right">{formatINR(bill.amount)}</p>
                    </div>
                  </Card>
                ))}
                <Card className="p-3 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-primary/20">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground">Total Outstanding</span>
                    <span className="text-sm font-bold text-primary">{formatINR(totalAmount)}</span>
                  </div>
                </Card>
              </div>
              
              {/* Desktop Table View */}
              <div className="hidden md:block relative overflow-hidden rounded-xl border border-border/50">
                <div className="overflow-y-auto max-h-[calc(90vh-220px)]">
                  <table className="w-full">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-muted">
                        <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted">#</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted">Bill No.</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted">Date</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted">Amount</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted">Days</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/30">
                      {bills.map((bill, billIdx) => (
                        <tr 
                          key={billIdx} 
                          className="group hover:bg-gradient-to-r hover:from-primary/5 hover:to-transparent transition-all duration-200"
                        >
                          <td className="py-1.5 px-3">
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-muted text-xs font-medium">
                              {billIdx + 1}
                            </span>
                          </td>
                          <td className="py-1.5 px-3">
                            <span className="text-sm font-medium text-foreground">{bill.billNo}</span>
                          </td>
                          <td className="py-1.5 px-3">
                            <span className="text-sm text-muted-foreground">{bill.date}</span>
                          </td>
                          <td className="py-1.5 px-3 text-right">
                            <span className="text-sm font-bold text-primary">{formatINR(bill.amount)}</span>
                          </td>
                          <td className="py-1.5 px-3 text-right">
                            <Badge className={cn("text-xs px-2 py-0", getDaysBadgeStyle(bill.days))}>
                              {bill.days} days
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-t-2 border-primary/20">
                        <td colSpan={3} className="py-2 px-3">
                          <span className="text-sm font-semibold text-foreground">Total Outstanding</span>
                        </td>
                        <td className="py-2 px-3 text-right">
                          <span className="text-sm font-bold text-primary">{formatINR(totalAmount)}</span>
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted/50 mb-4">
                <Inbox className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">No bill details available</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Aging filter type
type AgingFilterType = "lt15" | "d15_30" | "d30_60" | "d60_90" | "gt90" | null;

const Outstanding = () => {
  const [search, setSearch] = useState("");
  const [agingFilter, setAgingFilter] = useState<AgingFilterType>(null);
  const [townFilter, setTownFilter] = useState<string | null>(null);
  const [sendingWhatsApp, setSendingWhatsApp] = useState<string | null>(null);
  const [sendingPdf, setSendingPdf] = useState<string | null>(null);
  const [shareMenuKey, setShareMenuKey] = useState<string | null>(null);

  // Get firmId from user
  const user = authService.getCurrentUser();
  const firmId = user?.firmId;

  // Fetch outstanding data from API
  const { data: outstandingData, isLoading, error } = useOutstanding({ firmId });

  // Transform API response to component format
  const outstanding: TransformedOutstanding[] = useMemo(() => {
    if (!outstandingData) return [];
    
    return outstandingData.map((item) => ({
      customer: item.partyName,
      partyCode: item.partyCode || "",
      city: item.city || "",
      mobile: item.mobileNumber || "",
      total: item.outstandingAmt,
      aging: {
        lt15: item.lessthan15,
        d15_30: item.days15_30,
        d30_60: item.days30_60,
        d60_90: item.days60_90,
        gt90: item.greaterThan90,
      },
      bills: [],
    }));
  }, [outstandingData]);

  // Get unique towns from data
  const uniqueTowns = useMemo(() => {
    const towns = outstanding
      .map((item) => item.city)
      .filter((city) => city && city.trim() !== "")
      .filter((city, index, self) => self.indexOf(city) === index)
      .sort();
    return towns;
  }, [outstanding]);

  // Calculate summary metrics
  const summary = useMemo(() => {
    return outstanding.reduce(
      (acc, item) => ({
        total: acc.total + item.total,
        lt15: acc.lt15 + item.aging.lt15,
        d15_30: acc.d15_30 + item.aging.d15_30,
        d30_60: acc.d30_60 + item.aging.d30_60,
        d60_90: acc.d60_90 + item.aging.d60_90,
        gt90: acc.gt90 + item.aging.gt90,
      }),
      { total: 0, lt15: 0, d15_30: 0, d30_60: 0, d60_90: 0, gt90: 0 }
    );
  }, [outstanding]);

  const filteredOutstanding = outstanding.filter((item) => {
    // Search filter
    const matchesSearch =
      item.customer.toLowerCase().includes(search.toLowerCase()) ||
      item.mobile.includes(search);
    
    // Aging filter
    const matchesAging = !agingFilter || (() => {
      switch (agingFilter) {
        case "lt15": return item.aging.lt15 > 0;
        case "d15_30": return item.aging.d15_30 > 0;
        case "d30_60": return item.aging.d30_60 > 0;
        case "d60_90": return item.aging.d60_90 > 0;
        case "gt90": return item.aging.gt90 > 0;
        default: return true;
      }
    })();
    
    // Town filter
    const matchesTown = !townFilter || item.city === townFilter;
    
    return matchesSearch && matchesAging && matchesTown;
  });

  // Toggle aging filter
  const handleAgingFilter = (filter: AgingFilterType) => {
    setAgingFilter(agingFilter === filter ? null : filter);
  };

  // Get aging filter label
  const getAgingFilterLabel = () => {
    switch (agingFilter) {
      case "lt15": return "<15 Days";
      case "d15_30": return "15-30 Days";
      case "d30_60": return "30-60 Days";
      case "d60_90": return "60-90 Days";
      case "gt90": return ">90 Days";
      default: return null;
    }
  };

  // Handle WhatsApp send
  const handleSendWhatsApp = async (partyCode: string, mobile: string) => {
    if (!partyCode) {
      toast.error("Party code is required to send WhatsApp message");
      return;
    }

    if (!firmId) {
      toast.error("Firm information is missing. Please re-login and try again.");
      return;
    }

    if (!mobile) {
      toast.error("Customer mobile number is missing");
      return;
    }

    // Extract numeric part from mobile number (remove any non-numeric characters)
    const mobileNumber = mobile.replace(/\D/g, "");

    if (!mobileNumber) {
      toast.error("Invalid customer mobile number");
      return;
    }

    setShareMenuKey(null);
    setSendingWhatsApp(partyCode);

    try {
      // Call new endpoint: Common/whatsapp-message?firmId={firmId}&orderNo=0&mode=3&prefix={partyCode}
      const response = await outstandingService.getWhatsAppMessage({
        firmId,
        orderNo: 0,
        mode: 3,
        prefix: partyCode,
      });

      const message =
        response?.whatsappText ||
        `Dear ${partyCode}, your outstanding details are available. Please clear your dues at the earliest.`;

      // Try opening WhatsApp Desktop app first, then fallback to web
      const appUrl = `whatsapp://send?phone=91${mobileNumber}&text=${encodeURIComponent(
        message
      )}`;
      const webUrl = `https://web.whatsapp.com/send?phone=91${mobileNumber}&text=${encodeURIComponent(
        message
      )}`;

      // Try opening desktop app
      window.location.href = appUrl;

      // Fallback to web after delay if app doesn't open
      setTimeout(() => {
        window.open(webUrl, "_blank");
      }, 1500);

      toast.success(
        <div className="flex items-start gap-3 w-full">
          <div className="flex-shrink-0 mt-0.5">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg shadow-green-500/30">
              <CheckCircle2 className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-sm font-semibold text-foreground">Opening WhatsApp Web</h4>
            </div>
            <p className="text-sm text-muted-foreground">
              We&apos;ve opened WhatsApp Web with the outstanding message for the customer.
            </p>
          </div>
        </div>,
        {
          position: "top-center",
          duration: 4000,
          className:
            "bg-gradient-to-br from-background via-background to-green-500/5 border border-green-500/20 shadow-xl shadow-green-500/10 backdrop-blur-xl",
          style: {
            borderRadius: "12px",
            padding: "16px",
          },
        }
      );
    } catch (error) {
      console.error("Error getting WhatsApp message:", error);
      toast.error(
        <div className="flex items-start gap-3 w-full">
          <div className="flex-shrink-0 mt-0.5">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-rose-600 shadow-lg shadow-red-500/30">
              <XCircle className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-sm font-semibold text-foreground">Error Occurred</h4>
            </div>
            <p className="text-sm text-muted-foreground">
              Failed to prepare WhatsApp message. Please check your connection and try again.
            </p>
          </div>
        </div>,
        {
          position: "top-center",
          duration: 4000,
          className:
            "bg-gradient-to-br from-background via-background to-red-500/5 border border-red-500/20 shadow-xl shadow-red-500/10 backdrop-blur-xl",
          style: {
            borderRadius: "12px",
            padding: "16px",
          },
        }
      );
    } finally {
      setSendingWhatsApp(null);
    }
  };

  const showShareToast = (
    variant: "success" | "error",
    title: string,
    description: string
  ) => {
    const isSuccess = variant === "success";
    const Icon = isSuccess ? CheckCircle2 : XCircle;

    toast[variant](
      <div className="flex items-start gap-3 w-full">
        <div className="flex-shrink-0 mt-0.5">
          <div
            className={cn(
              "flex items-center justify-center w-10 h-10 rounded-full shadow-lg",
              isSuccess
                ? "bg-gradient-to-br from-green-500 to-emerald-600 shadow-green-500/30"
                : "bg-gradient-to-br from-red-500 to-rose-600 shadow-red-500/30"
            )}
          >
            <Icon className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-foreground mb-1">{title}</h4>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>,
      {
        position: "top-center",
        duration: 4000,
        className: cn(
          "backdrop-blur-xl shadow-xl",
          isSuccess
            ? "bg-gradient-to-br from-background via-background to-green-500/5 border border-green-500/20 shadow-green-500/10"
            : "bg-gradient-to-br from-background via-background to-red-500/5 border border-red-500/20 shadow-red-500/10"
        ),
        style: {
          borderRadius: "12px",
          padding: "16px",
        },
      }
    );
  };

  const handleSendPdf = async (partyCode: string) => {
    if (!partyCode) {
      toast.error("Party code is required to send the outstanding PDF");
      return;
    }

    if (!firmId) {
      toast.error("Firm information is missing. Please re-login and try again.");
      return;
    }

    setShareMenuKey(null);
    setSendingPdf(partyCode);

    try {
      const response = await outstandingService.sendOutstandingFile({
        firmId,
        partyCode,
        mode: 4,
      });

      if (!response.success) {
        showShareToast(
          "error",
          "PDF not sent",
          response.message || "Failed to send the outstanding file. Please try again."
        );
        return;
      }

      showShareToast(
        "success",
        "PDF sent",
        response.message || "Outstanding file sent successfully."
      );
    } catch (error) {
      console.error("Error sending outstanding file:", error);
      const message =
        error && typeof error === "object" && "message" in error
          ? String((error as { message: unknown }).message)
          : "Failed to send the outstanding file. Please check your connection and try again.";
      showShareToast("error", "PDF not sent", message);
    } finally {
      setSendingPdf(null);
    }
  };

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-8rem)] sm:h-[calc(100vh-8rem)] overflow-hidden">
        {/* Header Section */}
        <div className="flex-shrink-0 space-y-3 sm:space-y-4 pb-3 sm:pb-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">Outstanding Receivable</h2>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                {isLoading ? "Loading..." : `${outstanding.length} customers with pending dues`}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 sm:ml-auto">
              {/* Active filter indicators */}
              {agingFilter && (
                <Badge 
                  variant="secondary" 
                  className={cn(
                    "h-9 sm:h-10 px-2 sm:px-3 gap-1.5 sm:gap-2 cursor-pointer transition-all duration-200 animate-fade-up shrink-0",
                    agingFilter === "lt15" && "bg-green-500/15 text-green-600 dark:text-green-400 hover:bg-green-500/25 border border-green-500/30",
                    agingFilter === "d15_30" && "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-500/25 border border-cyan-500/30",
                    agingFilter === "d30_60" && "bg-amber-500/15 text-amber-600 dark:text-amber-400 hover:bg-amber-500/25 border border-amber-500/30",
                    agingFilter === "d60_90" && "bg-orange-500/15 text-orange-600 dark:text-orange-400 hover:bg-orange-500/25 border border-orange-500/30",
                    agingFilter === "gt90" && "bg-red-500/15 text-red-600 dark:text-red-400 hover:bg-red-500/25 border border-red-500/30"
                  )}
                  onClick={() => setAgingFilter(null)}
                >
                  <Filter className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  <span className="text-xs sm:text-sm font-medium">{getAgingFilterLabel()}</span>
                  <X className="h-3 w-3 sm:h-3.5 sm:w-3.5 ml-0.5 sm:ml-1" />
                </Badge>
              )}
              {townFilter && (
                <Badge 
                  variant="secondary" 
                  className="h-9 sm:h-10 px-2 sm:px-3 gap-1.5 sm:gap-2 cursor-pointer transition-all duration-200 animate-fade-up shrink-0 bg-blue-500/15 text-blue-600 dark:text-blue-400 hover:bg-blue-500/25 border border-blue-500/30"
                  onClick={() => setTownFilter(null)}
                >
                  <Filter className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  <span className="text-xs sm:text-sm font-medium">Town: {townFilter}</span>
                  <X className="h-3 w-3 sm:h-3.5 sm:w-3.5 ml-0.5 sm:ml-1" />
                </Badge>
              )}
              <div className="relative w-full sm:w-80">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-accent/20 rounded-lg blur-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-500" />
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or mobile..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 h-9 sm:h-10 bg-background/50 backdrop-blur-sm border-border/50 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-sm sm:text-base"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid gap-2 sm:gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            {isLoading ? (
              <>
                {[...Array(6)].map((_, i) => (
                  <StatCardSkeleton key={i} />
                ))}
              </>
            ) : (
              <>
                <StatCard
                  title="O/S As on Date"
                  value={formatLakhsCompact(summary.total)}
                  icon={PackageX}
                  variant="blue"
                  className="animate-fade-up stagger-1"
                />
                <StatCard
                  title="<15 Days"
                  value={formatLakhsCompact(summary.lt15)}
                  icon={Sparkles}
                  variant="green"
                  className="animate-fade-up stagger-2"
                />
                <StatCard
                  title="15-30 Days"
                  value={formatLakhsCompact(summary.d15_30)}
                  icon={Calendar}
                  variant="cyan"
                  className="animate-fade-up stagger-3"
                />
                <StatCard
                  title="30-60 Days"
                  value={formatLakhsCompact(summary.d30_60)}
                  icon={AlertTriangle}
                  variant="amber"
                  className="animate-fade-up stagger-4"
                />
                <StatCard
                  title="60-90 Days"
                  value={formatLakhsCompact(summary.d60_90)}
                  icon={Timer}
                  variant="orange"
                  className="animate-fade-up stagger-5"
                />
                <StatCard
                  title=">90 Days"
                  value={formatLakhsCompact(summary.gt90)}
                  icon={AlertOctagon}
                  variant="red"
                  className="animate-fade-up stagger-6"
                />
              </>
            )}
          </div>
        </div>

        {/* Outstanding List */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {isLoading ? (
            <>
              {/* Mobile Loading Skeleton */}
              <div className="block md:hidden flex-1 overflow-y-auto space-y-3 pb-4">
                {[...Array(5)].map((_, i) => (
                  <Card key={i} className="glass-card border-white/10 overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#0072B5] via-[#0090B5] to-[#00B5B0]" />
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 rounded-full bg-muted animate-pulse" />
                          <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                        </div>
                        <div className="h-3 w-24 bg-muted rounded animate-pulse" />
                        <div className="grid grid-cols-2 gap-2 pt-3 border-t border-border/30">
                          {[...Array(5)].map((_, j) => (
                            <div key={j} className="flex items-center justify-between">
                              <div className="h-3 w-12 bg-muted rounded animate-pulse" />
                              <div className="h-3 w-16 bg-muted rounded animate-pulse" />
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {/* Loading Skeleton */}
              <Card className="glass-card border-white/10 overflow-hidden flex-1 flex flex-col">
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#0072B5] via-[#0090B5] to-[#00B5B0]" />
                <div className="relative w-full flex-1 overflow-hidden flex flex-col">
                  <div className="overflow-x-auto overflow-y-auto flex-1">
                    <table className="w-full min-w-[800px]">
                      <thead className="sticky top-0 z-20">
                        <tr className="bg-muted">
                          <th className="h-10 px-4 text-left align-middle text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted">#</th>
                          <th className="h-10 px-4 text-left align-middle text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted">Customer</th>
                          <th className="h-10 px-4 text-left align-middle text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted">
                            <span className="inline-flex items-center gap-1 justify-start">Town <Filter className="h-2.5 w-2.5 opacity-50" /></span>
                          </th>
                          <th className="h-10 px-2 text-left align-middle text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted">Mobile</th>
                          <th className="h-10 px-4 text-right align-middle text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted">Outstanding</th>
                          <th className="h-10 px-2 text-right align-middle text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider bg-muted">
                            <span className="inline-flex items-center gap-1 justify-end">&lt;15 <Filter className="h-2.5 w-2.5 opacity-50" /></span>
                          </th>
                          <th className="h-10 px-2 text-right align-middle text-xs font-semibold text-cyan-600 dark:text-cyan-400 uppercase tracking-wider bg-muted">
                            <span className="inline-flex items-center gap-1 justify-end">15-30 <Filter className="h-2.5 w-2.5 opacity-50" /></span>
                          </th>
                          <th className="h-10 px-2 text-right align-middle text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider bg-muted">
                            <span className="inline-flex items-center gap-1 justify-end">30-60 <Filter className="h-2.5 w-2.5 opacity-50" /></span>
                          </th>
                          <th className="h-10 px-2 text-right align-middle text-xs font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wider bg-muted">
                            <span className="inline-flex items-center gap-1 justify-end">60-90 <Filter className="h-2.5 w-2.5 opacity-50" /></span>
                          </th>
                          <th className="h-10 px-2 text-right align-middle text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wider bg-muted">
                            <span className="inline-flex items-center gap-1 justify-end">&gt;90 <Filter className="h-2.5 w-2.5 opacity-50" /></span>
                          </th>
                          <th className="h-10 px-2 text-center align-middle text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...Array(8)].map((_, i) => (
                          <TableRowSkeleton key={i} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Card>
            </>
          ) : error ? (
            <Card className="glass-card border-white/10 flex-1 flex items-center justify-center">
              <CardContent className="text-center py-16">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-destructive/10 mb-6">
                  <AlertCircle className="h-10 w-10 text-destructive" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Failed to Load Data</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  There was an error loading the outstanding data. Please check your connection and try again.
                </p>
              </CardContent>
            </Card>
          ) : filteredOutstanding.length === 0 ? (
            <Card className="glass-card border-white/10 flex-1 flex items-center justify-center">
              <CardContent className="text-center py-16">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted/50 mb-6">
                  <Users className="h-10 w-10 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No Records Found</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  {search 
                    ? `No customers matching "${search}" were found.` 
                    : agingFilter 
                      ? `No customers with outstanding in ${getAgingFilterLabel()} bucket.`
                      : townFilter
                        ? `No customers found in ${townFilter}.`
                        : "There are no outstanding receivables at this time."}
                </p>
                {(agingFilter || townFilter) && (
                  <div className="flex gap-2 justify-center mt-4">
                    {agingFilter && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAgingFilter(null)}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Clear Aging Filter
                      </Button>
                    )}
                    {townFilter && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setTownFilter(null)}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Clear Town Filter
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="relative glass-card border-white/10 overflow-hidden flex-1 flex flex-col animate-fade-up">
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#0072B5] via-[#0090B5] to-[#00B5B0]" />
              <div className="relative w-full flex-1 overflow-hidden flex flex-col">
                <div className="overflow-x-auto overflow-y-auto flex-1">
                  <table className="w-full min-w-[800px]">
                    <thead className="sticky top-0 z-20">
                      <tr className="bg-muted">
                        <th className="w-[40px] sm:w-[50px] h-9 sm:h-10 px-2 sm:px-4 text-left align-middle text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted">#</th>
                        <th className="min-w-[120px] sm:min-w-[150px] h-9 sm:h-10 px-2 sm:px-4 text-left align-middle text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted">Customer</th>
                        {/*
                        <th 
                          className={cn(
                            "w-[90px] sm:w-[110px] h-9 sm:h-10 px-2 sm:px-4 text-left align-middle text-[10px] sm:text-xs font-semibold uppercase tracking-wider cursor-pointer select-none transition-all duration-200 group touch-manipulation",
                            townFilter 
                              ? "text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-950 ring-2 ring-inset ring-blue-500/50" 
                              : "text-muted-foreground bg-muted hover:bg-blue-50 dark:hover:bg-blue-950/50 active:bg-blue-100 dark:active:bg-blue-950"
                          )}
                        >
                          <Popover>
                            <PopoverTrigger asChild>
                              <div className="flex items-center gap-1 justify-start w-full">
                                <span>Town</span>
                                {townFilter 
                                  ? <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-blue-500 animate-pulse" />
                                  : <Filter className="h-2 w-2 sm:h-2.5 sm:w-2.5 opacity-40 group-hover:opacity-70 transition-opacity" />
                                }
                              </div>
                            </PopoverTrigger>
                            <PopoverContent className="w-56 p-2" align="start">
                              <div className="max-h-[300px] overflow-y-auto">
                                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border/50 mb-1">
                                  Filter by Town
                                </div>
                                <button
                                  onClick={() => setTownFilter(null)}
                                  className={cn(
                                    "w-full text-left px-2 py-1.5 text-sm rounded-md transition-colors hover:bg-muted",
                                    !townFilter && "bg-primary/10 text-primary font-medium"
                                  )}
                                >
                                  All Towns
                                </button>
                                {uniqueTowns.map((town) => (
                                  <button
                                    key={town}
                                    onClick={() => setTownFilter(town)}
                                    className={cn(
                                      "w-full text-left px-2 py-1.5 text-sm rounded-md transition-colors hover:bg-muted",
                                      townFilter === town && "bg-primary/10 text-primary font-medium"
                                    )}
                                  >
                                    {town}
                                  </button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </th>*/}
                        <th className="w-[90px] sm:w-[110px] h-9 sm:h-10 px-1 sm:px-2 text-left align-middle text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted">Mobile</th>
                        <th className="w-[100px] sm:w-[120px] h-9 sm:h-10 px-2 sm:px-4 text-right align-middle text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted">Outstanding</th>
                        <th 
                          onClick={() => handleAgingFilter("lt15")}
                          className={cn(
                            "w-[70px] sm:w-[90px] h-9 sm:h-10 px-1 sm:px-2 text-right align-middle text-[10px] sm:text-xs font-semibold uppercase tracking-wider cursor-pointer select-none transition-all duration-200 group touch-manipulation",
                            agingFilter === "lt15" 
                              ? "text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-950 ring-2 ring-inset ring-green-500/50" 
                              : "text-green-600 dark:text-green-400 bg-muted hover:bg-green-50 dark:hover:bg-green-950/50 active:bg-green-100 dark:active:bg-green-950"
                          )}
                        >
                          <span className="inline-flex items-center gap-0.5 sm:gap-1 justify-end">
                            <span className="hidden sm:inline">&lt;</span>15
                            {agingFilter === "lt15" 
                              ? <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-green-500 animate-pulse" />
                              : <Filter className="h-2 w-2 sm:h-2.5 sm:w-2.5 opacity-40 group-hover:opacity-70 transition-opacity" />
                            }
                          </span>
                        </th>
                        <th 
                          onClick={() => handleAgingFilter("d15_30")}
                          className={cn(
                            "w-[70px] sm:w-[90px] h-9 sm:h-10 px-1 sm:px-2 text-right align-middle text-[10px] sm:text-xs font-semibold uppercase tracking-wider cursor-pointer select-none transition-all duration-200 group touch-manipulation",
                            agingFilter === "d15_30" 
                              ? "text-cyan-600 dark:text-cyan-400 bg-cyan-100 dark:bg-cyan-950 ring-2 ring-inset ring-cyan-500/50" 
                              : "text-cyan-600 dark:text-cyan-400 bg-muted hover:bg-cyan-50 dark:hover:bg-cyan-950/50 active:bg-cyan-100 dark:active:bg-cyan-950"
                          )}
                        >
                          <span className="inline-flex items-center gap-0.5 sm:gap-1 justify-end">
                            15-30
                            {agingFilter === "d15_30" 
                              ? <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-cyan-500 animate-pulse" />
                              : <Filter className="h-2 w-2 sm:h-2.5 sm:w-2.5 opacity-40 group-hover:opacity-70 transition-opacity" />
                            }
                          </span>
                        </th>
                        <th 
                          onClick={() => handleAgingFilter("d30_60")}
                          className={cn(
                            "w-[70px] sm:w-[90px] h-9 sm:h-10 px-1 sm:px-2 text-right align-middle text-[10px] sm:text-xs font-semibold uppercase tracking-wider cursor-pointer select-none transition-all duration-200 group touch-manipulation",
                            agingFilter === "d30_60" 
                              ? "text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-950 ring-2 ring-inset ring-amber-500/50" 
                              : "text-amber-600 dark:text-amber-400 bg-muted hover:bg-amber-50 dark:hover:bg-amber-950/50 active:bg-amber-100 dark:active:bg-amber-950"
                          )}
                        >
                          <span className="inline-flex items-center gap-0.5 sm:gap-1 justify-end">
                            30-60
                            {agingFilter === "d30_60" 
                              ? <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-amber-500 animate-pulse" />
                              : <Filter className="h-2 w-2 sm:h-2.5 sm:w-2.5 opacity-40 group-hover:opacity-70 transition-opacity" />
                            }
                          </span>
                        </th>
                        <th 
                          onClick={() => handleAgingFilter("d60_90")}
                          className={cn(
                            "w-[70px] sm:w-[90px] h-9 sm:h-10 px-1 sm:px-2 text-right align-middle text-[10px] sm:text-xs font-semibold uppercase tracking-wider cursor-pointer select-none transition-all duration-200 group touch-manipulation",
                            agingFilter === "d60_90" 
                              ? "text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-950 ring-2 ring-inset ring-orange-500/50" 
                              : "text-orange-600 dark:text-orange-400 bg-muted hover:bg-orange-50 dark:hover:bg-orange-950/50 active:bg-orange-100 dark:active:bg-orange-950"
                          )}
                        >
                          <span className="inline-flex items-center gap-0.5 sm:gap-1 justify-end">
                            60-90
                            {agingFilter === "d60_90" 
                              ? <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-orange-500 animate-pulse" />
                              : <Filter className="h-2 w-2 sm:h-2.5 sm:w-2.5 opacity-40 group-hover:opacity-70 transition-opacity" />
                            }
                          </span>
                        </th>
                        <th 
                          onClick={() => handleAgingFilter("gt90")}
                          className={cn(
                            "w-[80px] sm:w-[100px] h-9 sm:h-10 px-1 sm:px-2 text-right align-middle text-[10px] sm:text-xs font-semibold uppercase tracking-wider cursor-pointer select-none transition-all duration-200 group touch-manipulation",
                            agingFilter === "gt90" 
                              ? "text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-950 ring-2 ring-inset ring-red-500/50" 
                              : "text-red-600 dark:text-red-400 bg-muted hover:bg-red-50 dark:hover:bg-red-950/50 active:bg-red-100 dark:active:bg-red-950"
                          )}
                        >
                          <span className="inline-flex items-center gap-0.5 sm:gap-1 justify-end">
                            <span className="hidden sm:inline">&gt;</span>90
                            {agingFilter === "gt90" 
                              ? <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-red-500 animate-pulse" />
                              : <Filter className="h-2 w-2 sm:h-2.5 sm:w-2.5 opacity-40 group-hover:opacity-70 transition-opacity" />
                            }
                          </span>
                        </th>
                        <th className="w-[80px] sm:w-[100px] h-9 sm:h-10 px-1 sm:px-2 text-center align-middle text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/30">
                      {filteredOutstanding.map((item, idx) => (
                        <tr
                          key={idx}
                          className="group transition-all duration-200 hover:bg-gradient-to-r hover:from-primary/5 hover:via-primary/3 hover:to-transparent"
                        >
                          <td className="px-2 sm:px-4 py-1.5">
                            <span className="inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-muted/50 text-[10px] sm:text-xs font-medium text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                              {idx + 1}
                            </span>
                          </td>
                          <td className="px-2 sm:px-4 py-1.5">
                            <div className="flex items-center gap-1.5 sm:gap-2 overflow-hidden">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="font-semibold text-xs sm:text-sm text-foreground truncate cursor-default group-hover:text-primary transition-colors">
                                      {item.customer}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-[400px] backdrop-blur-xl bg-popover/95 border-white/20">
                                    <p>{item.customer}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              {item.partyCode && (
                                <Badge variant="outline" className="shrink-0 text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0 h-3.5 sm:h-4 bg-muted/50 border-border/50 text-muted-foreground font-normal">
                                  {item.partyCode}
                                </Badge>
                              )}
                            </div>
                          </td>
{/*
                          <td className="px-2 sm:px-4 py-1.5">
                            <span className="text-xs sm:text-sm text-muted-foreground truncate">
                              {item.city || "-"}
                            </span>
                          </td> 
                          */}
                          <td className="px-1 sm:px-2 py-1.5">
                            <span className="text-xs sm:text-sm text-muted-foreground font-mono">
                              {item.mobile || "-"}
                            </span>
                          </td> 
                          <td className="px-2 sm:px-4 py-1.5 text-right">
                            <span className="font-bold text-primary text-xs sm:text-sm tracking-tight">
                              {formatINR(item.total)}
                            </span>
                          </td>
                          <td className="px-1 sm:px-2 py-1.5 text-right">
                            <AgingValue value={item.aging.lt15} variant="green" />
                          </td>
                          <td className="px-1 sm:px-2 py-1.5 text-right">
                            <AgingValue value={item.aging.d15_30} variant="cyan" />
                          </td>
                          <td className="px-1 sm:px-2 py-1.5 text-right">
                            <AgingValue value={item.aging.d30_60} variant="amber" />
                          </td>
                          <td className="px-1 sm:px-2 py-1.5 text-right">
                            <AgingValue value={item.aging.d60_90} variant="orange" />
                          </td>
                          <td className="px-1 sm:px-2 py-1.5 text-right">
                            {item.aging.gt90 > 0 ? (
                              <Badge className="bg-gradient-to-r from-red-500 to-rose-600 text-white border-0 shadow-md shadow-red-500/20 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0">
                                {formatINR(item.aging.gt90)}
                              </Badge>
                            ) : (
                              <span className="text-xs sm:text-sm text-muted-foreground/40">-</span>
                            )}
                          </td>
                          <td className="px-1 sm:px-2 py-1.5">
                            <div className="flex gap-1 sm:gap-1.5 justify-center">
                              <BillDetailsDialog
                                customer={item.customer}
                                partyCode={item.partyCode}
                                firmId={firmId}
                                total={item.total}
                              />
                              {(() => {
                                const menuKey = `${item.partyCode}-${idx}`;
                                const isSending =
                                  sendingWhatsApp === item.partyCode || sendingPdf === item.partyCode;

                                return (
                                  <Popover
                                    open={shareMenuKey === menuKey}
                                    onOpenChange={(open) => setShareMenuKey(open ? menuKey : null)}
                                  >
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <PopoverTrigger asChild>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className={cn(
                                                "relative h-7 w-7 sm:h-8 sm:w-8 p-0 rounded-lg sm:rounded-xl",
                                                "bg-gradient-to-br from-background/95 via-background/90 to-green-500/5",
                                                "border border-green-500/20 dark:border-green-500/30",
                                                "backdrop-blur-sm",
                                                "transition-all duration-300 ease-out",
                                                "hover:border-green-500/50 hover:bg-green-500/10",
                                                "hover:shadow-lg hover:shadow-green-500/20",
                                                "hover:-translate-y-0.5 hover:scale-105",
                                                "active:scale-95",
                                                "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:scale-100",
                                                "group/wa overflow-hidden touch-manipulation"
                                              )}
                                              disabled={isSending || !item.partyCode}
                                              aria-label="Send WhatsApp message"
                                            >
                                              <div className="absolute inset-0 rounded-lg sm:rounded-xl opacity-0 group-hover/wa:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 p-[1px]">
                                                <div className="absolute inset-[1px] rounded-lg sm:rounded-xl bg-background/95" />
                                              </div>
                                              <div
                                                className="absolute inset-0 rounded-lg sm:rounded-xl opacity-0 group-hover/wa:opacity-60 transition-opacity duration-500 blur-md"
                                                style={{
                                                  background: "radial-gradient(circle, rgba(34,197,94,0.3), transparent 70%)",
                                                }}
                                              />
                                              <div className="relative z-10 flex items-center justify-center">
                                                {isSending ? (
                                                  <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-600 dark:text-green-400 animate-spin" strokeWidth={2.5} />
                                                ) : (
                                                  <MessageCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground group-hover/wa:text-green-600 dark:group-hover/wa:text-green-400 transition-all duration-300 group-hover/wa:scale-110" strokeWidth={2.5} />
                                                )}
                                              </div>
                                            </Button>
                                          </PopoverTrigger>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="backdrop-blur-xl bg-popover/95 border-white/20">
                                          <p className="text-xs font-medium">Send WhatsApp Message</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                    <PopoverContent
                                      side="left"
                                      align="center"
                                      className="w-auto p-1 rounded-lg border border-border/60 bg-popover/95 backdrop-blur-xl shadow-lg"
                                    >
                                      <div className="flex items-center gap-1">
                                        <button
                                          type="button"
                                          onClick={() => handleSendPdf(item.partyCode)}
                                          disabled={isSending}
                                          className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-xs font-medium text-foreground whitespace-nowrap transition-colors hover:bg-rose-500/10 hover:text-rose-600 dark:hover:text-rose-400 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none"
                                        >
                                          <FileDown className="h-3.5 w-3.5 text-rose-500" strokeWidth={2.25} />
                                          PDF
                                        </button>
                                        <span className="h-4 w-px bg-border/80" />
                                        <button
                                          type="button"
                                          onClick={() => handleSendWhatsApp(item.partyCode, item.mobile)}
                                          disabled={isSending || !item.mobile}
                                          className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-xs font-medium text-foreground whitespace-nowrap transition-colors hover:bg-green-500/10 hover:text-green-600 dark:hover:text-green-400 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none"
                                        >
                                          <MessageCircle className="h-3.5 w-3.5 text-green-500" strokeWidth={2.25} />
                                          Message
                                        </button>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Outstanding;
